import React, { useEffect, useState } from 'react'

interface IProps {
  /** Translated label for the pill button (e.g. `t('HERO.PRIMARY_CTA')`). */
  label: string
  /**
   * Element id of the lead-form section. The bar hides while this element
   * is intersecting (or near) the viewport so it doesn't compete with the
   * form the user is about to fill, and re-appears once the user has
   * scrolled past it. Defaults to `'contact-section'` (the B2B / Flotillas
   * convention); on-demand passes `'cotizar'`.
   */
  formId?: string
  /**
   * Minimum vertical scroll (px) before the bar reveals. Defaults to 600 —
   * roughly past a one-screen hero on mobile. B2B used 700 historically; the
   * extra 100 didn't materially change behavior so we collapse to one value.
   */
  revealAtPx?: number
  /**
   * Buffer (px) around the form's bounding rect. The form is considered
   * "near the viewport" when its top is within `viewportBottom + N` AND its
   * bottom is within `viewportTop - N`. Default 100.
   */
  formNearOffsetPx?: number
  /**
   * Click handler. Defaults to `scrollIntoView({ behavior: 'smooth' })` on
   * the form section. Override when the caller needs to fire analytics or
   * route to a different target (e.g. open a modal).
   */
  onClick?: () => void
}

const DEFAULT_FORM_ID = 'contact-section'
const DEFAULT_REVEAL_AT_PX = 600
const DEFAULT_FORM_NEAR_OFFSET_PX = 100

/**
 * Mobile-only sticky CTA bar shared by long-form landing pages
 * (`/anfitrion/negocio`, `/pension-estacionamiento/flotillas`,
 * `/renta-mini-bodegas/almacenamiento-a-domicilio`, and future B2B/B2C
 * landings).
 *
 *   - Full-width bar at `fixed inset-x-0 bottom-0 z-50`
 *   - Hairline `border-t border-ink-100/15`, `bg-white-500/95` with
 *     `backdrop-blur-sm` (editorial vocabulary — no heavy shadows)
 *   - Single full-width primary blue pill button (CTA-glow shadow)
 *   - Slide-up animation via `translate-y` + opacity
 *   - Reveals after the user scrolls past the hero (`revealAtPx`, default 600)
 *   - Hides when the lead form (`formId`) is intersecting or near the
 *     viewport so the bar doesn't compete with the form
 *
 * The form-intersection check is TWO-EDGE on purpose: it tests both
 * `rect.top < viewportBottom + offset` AND `rect.bottom > -offset`. A
 * single-edge `top < viewportBottom + offset` check works when the form
 * sits at the bottom of the page (you can't scroll past it), but on
 * landings where the form is in the hero (e.g. on-demand wizard), the
 * single edge stays true forever once you scroll past the form, hiding
 * the bar for the entire rest of the page. The two-edge check correctly
 * returns "near the form" only when the form actually intersects (or is
 * close to) the viewport.
 *
 * Also auto-hides when the page `<footer>` enters the viewport. Once the
 * user has reached the footer they've consumed the whole page; covering
 * the bottom 70px with a CTA bar there would hide links and legal copy
 * (terms, refunds, contact) the user is actively trying to read. The
 * sticky CTA's job is to nudge mid-page scrollers — at the footer it's
 * done. Targets `document.querySelector('footer')` so the page doesn't
 * need to wire anything; every Footer renders a `<footer>` root.
 *
 * Pages that render this MUST set `mobileBottom="bottom-24"` on the
 * global `<WhatsAppButton />` so the FAB sits above the bar on mobile.
 * Desktop is unaffected (`md:hidden` on the bar; `md:bottom-8` on the FAB).
 */
const MobileStickyCta = ({
  label,
  formId = DEFAULT_FORM_ID,
  revealAtPx = DEFAULT_REVEAL_AT_PX,
  formNearOffsetPx = DEFAULT_FORM_NEAR_OFFSET_PX,
  onClick
}: IProps) => {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > revealAtPx
      const formSection = document.getElementById(formId)
      let nearForm = false
      if (formSection) {
        const rect = formSection.getBoundingClientRect()
        nearForm =
          rect.bottom > -formNearOffsetPx &&
          rect.top < window.innerHeight + formNearOffsetPx
      }
      // Hide once the footer enters the viewport — the bar would cover
      // footer links / legal copy the user is trying to read.
      const footerEl = document.querySelector('footer')
      const inFooter =
        !!footerEl && footerEl.getBoundingClientRect().top < window.innerHeight
      setVisible(scrolled && !nearForm && !inFooter)
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [revealAtPx, formId, formNearOffsetPx])

  const handleClick = () => {
    if (onClick) {
      onClick()
      return
    }
    const section = document.getElementById(formId)
    if (section) section.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-50 border-t border-ink-100/15 bg-white-500/95 px-4 py-3 backdrop-blur-sm transition-all duration-300 md:hidden ${
        visible
          ? 'translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-full opacity-0'
      }`}
    >
      <button
        type="button"
        onClick={handleClick}
        className="w-full rounded-full bg-blue-500 py-3 text-center text-[15px] font-semibold text-white-500 shadow-[0_20px_40px_-16px_rgba(0,164,255,0.55)] transition-colors hover:bg-blue-600 active:scale-[0.98]"
      >
        {label}
      </button>
    </div>
  )
}

export default React.memo(MobileStickyCta)
